import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBrands } from '../contexts/BrandContext'
import { Forbidden } from '../components/common/Forbidden'
import { Icons } from '../components/icons'
import { can } from '../utils/permissions'
import { isForbidden } from '../utils/httpError'
import { brandColor, brandInitial } from '../utils/brand'
import { notifySuccess, notifyError } from '../utils/notify'
import { getAccount, updateAccount, resendInvite } from '../services/accountStore'
import type { Account } from '../types'

type LoadStatus = 'loading' | 'ready' | 'forbidden' | 'notfound'

const ACCOUNTS_ROUTE = '/admin/accounts'

/** Detailseite eines Accounts — gemountet an `/admin/accounts/:id` (Spec §7.3).
 *  BMC-Designsystem `console.css`. */
export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { session } = useAuth()
  const { resolveBrand } = useBrands()
  const [account, setAccount] = useState<Account | null>(null)
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [saving, setSaving] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [active, setActive] = useState(true)

  const allowed = session ? can(session).accountsView() : false

  useEffect(() => {
    if (!session || !allowed || !id) return
    let cancelled = false
    setStatus('loading')
    getAccount(id)
      .then((a) => {
        if (cancelled) return
        setAccount(a)
        setDisplayName(a.displayName ?? '')
        setActive(a.active)
        setStatus('ready')
      })
      .catch((err) => {
        if (!cancelled) setStatus(isForbidden(err) ? 'forbidden' : 'notfound')
      })
    return () => {
      cancelled = true
    }
  }, [session, allowed, id])

  const handleSave = useCallback(async () => {
    if (!id) return
    setSaving(true)
    try {
      const updated = await updateAccount(id, { displayName, active })
      setAccount(updated)
      notifySuccess('Account gespeichert')
    } catch {
      notifyError('Speichern fehlgeschlagen, bitte erneut.')
    } finally {
      setSaving(false)
    }
  }, [id, displayName, active])

  const handleResend = useCallback(async () => {
    if (!id) return
    setInviting(true)
    try {
      await resendInvite(id)
      notifySuccess('Einladung erneut gesendet')
    } catch {
      notifyError('Einladung konnte nicht gesendet werden, bitte erneut.')
    } finally {
      setInviting(false)
    }
  }, [id])

  if (!session) return null

  if (!allowed) return <Forbidden message="Kein Zugriff auf die Nutzerverwaltung." />

  if (status === 'loading') return statusPanel('Account', 'Account wird geladen …')
  if (status === 'forbidden') return <Forbidden message="Diesen Account darfst du nicht einsehen." />
  if (status === 'notfound' || !account) return statusPanel('Nicht gefunden', 'Account nicht gefunden.')

  const canEdit = session.role === 'admin'
  const brand = account.brandKey ? resolveBrand(account.brandKey) : null

  return (
    <div className="wrap-narrow">
      <section className="cover">
        <div className="eyebrow">Nutzerverwaltung</div>
        <h1>
          <span className="hl">Account</span>-Details
        </h1>
        <p>Details und Bearbeitung des Accounts.</p>
        <div className="cover-actions">
          <Link to={ACCOUNTS_ROUTE} className="btn onblue sm">
            {Icons.back} Zurück
          </Link>
        </div>
      </section>

      <section className="section">
        <div className="shead">
          <span className="sico">{Icons.id}</span>
          Stammdaten
        </div>
        <div className="flex flex-col gap-4">
          <div className="field">
            <label className="flabel" htmlFor="f_accountEmail">
              E-Mail
            </label>
            <input className="inp" id="f_accountEmail" value={account.email} disabled readOnly />
          </div>
          <div className="field">
            <label className="flabel" htmlFor="f_accountRole">
              Rolle
            </label>
            <input className="inp" id="f_accountRole" value={account.role} disabled readOnly />
          </div>
          {brand && (
            <div className="field">
              <label className="flabel" htmlFor="f_accountBrand">
                Brand
              </label>
              <div className="relative">
                <span
                  className="chip-brand"
                  style={{
                    background: brandColor(brand),
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  {brandInitial(brand)}
                </span>
                <input
                  className="inp"
                  id="f_accountBrand"
                  value={brand.brandName}
                  disabled
                  readOnly
                  style={{ paddingLeft: 44 }}
                />
              </div>
            </div>
          )}
          {account.vcardUrlKey && (
            <div className="field">
              <label className="flabel" htmlFor="f_accountVcardUrlKey">
                vCard-URL-Key
              </label>
              <input className="inp" id="f_accountVcardUrlKey" value={account.vcardUrlKey} disabled readOnly />
            </div>
          )}
          <div className="field">
            <label className="flabel" htmlFor="f_displayName">
              Anzeigename
            </label>
            <input
              className="inp"
              id="f_displayName"
              value={displayName}
              disabled={!canEdit}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shead">
          <span className="sico">{Icons.toggle}</span>
          Status
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium" style={{ color: 'var(--bmc-fg)' }}>
              Aktiv
            </div>
            <div className="fhint">Inaktive Accounts können sich nicht mehr anmelden</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={active}
            aria-label="Aktiv"
            className={active ? 'switch on' : 'switch'}
            disabled={!canEdit}
            onClick={() => setActive(!active)}
          />
        </div>
      </section>

      {canEdit && account.role !== 'user' && account.hasPassword === false && (
        <section className="section">
          <div className="shead">
            <span className="sico">{Icons.mail}</span>
            Einladung
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--bmc-fg)' }}>
                Einladung ausstehend
              </div>
              <div className="fhint">
                Dieses Konto hat noch kein Passwort gesetzt. Sende die Einladungs-Mail erneut (Link 7 Tage gültig).
              </div>
            </div>
            <button type="button" className="btn outline" disabled={inviting} onClick={handleResend}>
              {Icons.mail} Einladung erneut senden
            </button>
          </div>
        </section>
      )}

      {canEdit && (
        <div className="mt-4 flex items-center justify-end gap-2.5">
          <Link to={ACCOUNTS_ROUTE} className="btn ghost">
            Abbrechen
          </Link>
          <button type="button" className="btn primary" disabled={saving} onClick={handleSave}>
            Speichern
          </button>
        </div>
      )}
    </div>
  )
}

function statusPanel(title: string, text: string) {
  return (
    <section className="panel">
      <div className="head">
        <h2>{title}</h2>
      </div>
      <div className="empty">{text}</div>
    </section>
  )
}
